<template>
    <div class="employee-file-upload">
        <!-- 头部：标题和上传按钮 -->
        <div class="upload-header">
            <h4 class="upload-title">{{ title }}</h4>
            <div v-if="maxCount === undefined || fileList.length < maxCount" @click.stop>
                <el-upload ref="uploadRef" :show-file-list="false" :accept="accept" :disabled="disabled || uploading"
                    :auto-upload="false" :on-change="handleFileChange" :multiple="multiple"
                    :limit="maxCount ? maxCount - fileList.length : undefined" :on-exceed="handleExceed">
                    <el-button class="upload-btn" :disabled="disabled || uploading">
                        <img src="@/assets/icons/upload.svg" alt="upload" class="upload-icon" />
                        {{ t('common.upload') }}
                    </el-button>
                </el-upload>
            </div>
        </div>

        <!-- 提示文字 -->
        <div v-if="tips" class="tips-text">{{ tips }}</div>

        <!-- 文件列表 -->
        <div v-if="fileList && fileList.length > 0" class="file-list">
            <div v-for="file in fileList" :key="file.uid" class="file-item">
                <img src="@/assets/icons/file.svg" alt="file" class="file-icon" />
                <span class="file-name">{{ file.name || file.fileName }}</span>
                <button v-if="!disabled" type="button" class="delete-btn" @click="handleDelete(file)">
                    <Icon name="delete" size="sm" />
                </button>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { ElUpload, ElButton, ElMessage } from 'element-plus'
import { useI18n } from 'vue-i18n'
import { Icon } from '@/components/basic-components/icon'
import type { UploadedFile } from '@/components/business-components/visa-file-upload/types'

const { t } = useI18n()

interface Props {
    title: string
    tips?: string
    fileList?: UploadedFile[]
    accept?: string
    multiple?: boolean
    disabled?: boolean
    maxSize?: number // MB
    maxCount?: number // 最大上传数量
}

const props = withDefaults(defineProps<Props>(), {
    tips: '',
    fileList: () => [],
    accept: '', // 必须从外部传入
    multiple: true,
    disabled: false,
    maxSize: 10,
    maxCount: undefined // 不限制数量
})

const emit = defineEmits<{
    (e: 'update:fileList', fileList: UploadedFile[]): void
    (e: 'upload', file: File): void
    (e: 'delete', file: UploadedFile): void
}>()

const uploading = ref(false)
const uploadRef = ref<InstanceType<typeof ElUpload>>()

const handleExceed = () => {
    if (props.maxCount !== undefined) {
        ElMessage.error(t('common.maxUploadCountExceeded', [props.maxCount]))
    }
}

const handleFileChange = (uploadFile: { raw: File }) => {
    try {
        const rawFile = uploadFile.raw
        // 用户取消文件选择时，raw 为空，直接返回，避免触发任何事件
        if (!rawFile) {
            // 阻止事件传播，防止触发父组件的 cancel 或 close 事件
            return
        }
        // 验证最大上传数量（检查当前文件列表 + 本次选择的文件是否会超过限制）
        if (props.maxCount !== undefined && props.fileList.length + 1 > props.maxCount) {
            ElMessage.error(t('common.maxUploadCountExceeded', [props.maxCount]))
            return
        }

        // 验证文件类型
        if (props.accept) {
            const acceptedExtensions = props.accept.split(',').map(ext => ext.trim().toLowerCase())
            const fileExtension = '.' + rawFile.name.split('.').pop()?.toLowerCase()

            if (!acceptedExtensions.includes(fileExtension)) {
                const acceptText = props.accept.replace(/\./g, '').replace(/,/g, '、')
                ElMessage.error(t('common.fileFormatNotSupported', [acceptText]))
                return
            }
        }

        // 验证文件大小
        const maxSizeBytes = props.maxSize * 1024 * 1024
        if (rawFile.size > maxSizeBytes) {
            ElMessage.error(t('common.fileSizeExceeded', [props.maxSize]))
            return
        }

        // 触发上传事件
        emit('upload', rawFile)
    } catch (error) {
        // 捕获所有异常，防止错误冒泡导致弹窗关闭
        console.error('File change handler error:', error)
    } finally {
        // 清空 el-upload 内部文件列表，防止内部累积导致误触发 on-exceed
        uploadRef.value?.clearFiles()
    }
}

const handleDelete = (file: UploadedFile) => {
    const newFileList = props.fileList.filter((f) => f.url !== file.url)
    emit('update:fileList', newFileList)
    emit('delete', file)
}

// 设置上传的文件（供父组件调用）
const setUploadedFile = (file: UploadedFile) => {
    const newFileList = [...props.fileList, file]
    emit('update:fileList', newFileList)
}

// 设置上传进度（供父组件调用）
const setUploadProgress = (progress: number) => {
    uploading.value = progress > 0 && progress < 100
}

defineExpose({
    setUploadedFile,
    setUploadProgress
})
</script>

<style scoped lang="scss">
.employee-file-upload {

    .upload-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 12px;
        .upload-title {
            font-size: 14px;
            font-weight: normal;
            color: #495057;
            margin: 0;
        }

        .upload-btn {
            height: auto !important;
            display: flex;
            align-items: center;
            gap: 4px;
            color: var(--el-color-primary);
            border: none;
            padding: 0px 4px;
            background: transparent;
            font-weight: normal;

            .upload-icon {
                margin-right: 7px;
                width: 14px;
                height: 14px;
            }
        }
    }

    .tips-text {
        font-size: 12px;
        line-height: 1.6;
        color: #88929B;
        white-space: pre-line;
        //margin-bottom: 16px;
    }

    .file-list {
        margin-top: 10px;
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    .file-item {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 6px 8px;
        height: 32px;
        background: #FFFFFF;
        border-radius: 8px;

        .file-icon {
            flex-shrink: 0;
        }

        .file-name {
            flex: 1;
            font-size: 14px;
            color: #495057;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        .delete-btn {
            flex-shrink: 0;
            padding: 0;
            background: none;
            border: none;
            cursor: pointer;
            color: #86909C;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: color 0.2s;

            &:hover {
                //color: #F03D3E;
            }
        }
    }
}
</style>
